import { MutableRefObject, useState } from 'react'
import React from 'react'
import { Popover } from 'antd'
import { getClassNames } from '../../_utils/classnames'
import { SmileOutlined } from '@ant-design/icons'
import Picker from '@emoji-mart/react'
import data from '@emoji-mart/data'
import { EMOJI_PICKER_CONFIG } from '@pushly/aqe/lib/components/emoji-manager/emoji-picker-config'

import { preventAll } from '@pushly/aqe/lib/utils/prevent'
import './emoji-manager.scss'

const BASE_CLASSNAME = 'emoji-popover'

type EmojiPopover = {
    // uniquely identify this popover component for click events
    componentId: MutableRefObject<string>
    // allow custom value handling
    onChange: (...args: any[]) => void
}

/**
 * Allows use of EmojiPicker as an icon/button with popover, not as a controlled input component.
 */
export const EmojiPopover = (props: EmojiPopover) => {
    const { componentId, onChange } = props

    const [showEmojiOptions, setShowEmojiOptions] = useState(false)

    const handleToggleClick = React.useCallback(
        (ev: React.MouseEvent<HTMLSpanElement>) => {
            preventAll(ev)
            setShowEmojiOptions((prev) => !prev)
        },
        [componentId.current],
    )

    const handleEmojiClick = React.useCallback(
        (emoji: any) => {
            onChange(null, { emoji: emoji.native, annotation: emoji.name ?? '' })
        },
        [onChange],
    )

    // Determine theme from the document's data-theme attribute
    const pickerTheme =
        typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
            ? 'dark'
            : 'light'

    return (
        <Popover
            overlayClassName={getClassNames(`${BASE_CLASSNAME}-emoji-overlay`, componentId.current)}
            trigger="click"
            open={showEmojiOptions}
            destroyTooltipOnHide={false}
            onOpenChange={(v) => {
                if (!v) {
                    setShowEmojiOptions(false)
                }
            }}
            placement="bottomLeft"
            content={
                <div className={getClassNames(`${BASE_CLASSNAME}-emoji-picker-react`)}>
                    <Picker
                        data={data}
                        onEmojiSelect={handleEmojiClick}
                        theme={pickerTheme}
                        {...EMOJI_PICKER_CONFIG}
                    />
                </div>
            }
        >
            <span className={getClassNames(`${BASE_CLASSNAME}-emoji-toggle`, componentId.current)}>
                <SmileOutlined onClick={handleToggleClick} />
            </span>
        </Popover>
    )
}
